import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Box,
  CircularProgress,
  InputAdornment,
  Paper,
  Popper,
  TextField,
  Typography,
  Autocomplete as MUIAutocomplete, AutocompleteInputChangeReason } from '@mui/material';
import { makeStyles, useTheme } from '@mui/styles';
import { TFunction } from 'i18next';
import React, { FC, forwardRef, useEffect, useState } from 'react';
import { useHistory } from 'react-router';
import { OrderListingType } from 'types/order';
import { CURRENT_PATH, DEFAULT_ROUTE } from 'utils/constants';

const formatSearchCurrency = (value: number) => new Intl.NumberFormat('en-US', {
  currency: 'USD',
  maximumFractionDigits: 2,
  minimumFractionDigits: 2,
  style: 'currency',
}).format(value);

const useStyles = makeStyles((theme: any) => ({
  toolbarTextfield: { color: theme.palette.grey[50] },
  toolbarTextfieldFocused: { color: theme.accent.main },
  renderContainer: {
    borderTopWidth: 1,
    borderTopStyle: 'solid',
    borderTopColor: theme.palette.grey[400],
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(1, 2),
    width: '100%',
  },
  option: { padding: '0 !important' },
  renderLine: {
    minWidth: 0,
    '& + &': { marginTop: theme.spacing(0.5) },
  },
  renderValue: {
    color: theme.palette.primary.darker,
    fontWeight: 400,
    overflowWrap: 'anywhere',
  },
  numericValue: {
    fontFamily: '"Roboto Mono", "SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace',
    fontVariantNumeric: 'tabular-nums',
  },
  moneyItem: {
    alignItems: 'baseline',
    display: 'inline-flex',
    '& span:last-child': { marginLeft: theme.spacing(0.5) },
    [theme.breakpoints.down('md')]: {
      display: 'flex',
      justifyContent: 'space-between',
      width: '100%',
      '& span:last-child': { marginLeft: theme.spacing(1) },
    },
  },
  renderLabel: {
    color: theme.palette.primary.light,
    fontWeight: 400,
    marginRight: theme.spacing(0.5),
  },
  resultsListbox: {
    maxHeight: 'min(65vh, 520px)',
    overflowX: 'hidden',
    overflowY: 'scroll',
    position: 'relative',
    paddingRight: theme.spacing(2),
    WebkitOverflowScrolling: 'touch',
    scrollbarColor: `${theme.palette.primary.light} ${theme.palette.background.default}`,
    scrollbarGutter: 'stable',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: theme.spacing(1),
      right: theme.spacing(0.5),
      bottom: theme.spacing(1),
      width: theme.spacing(0.75),
      backgroundColor: theme.palette.background.default,
      borderRadius: theme.spacing(0.5),
      pointerEvents: 'none',
      zIndex: 1,
    },
    '&::after': {
      content: '""',
      position: 'absolute',
      top: theme.spacing(1),
      right: theme.spacing(0.5),
      width: theme.spacing(0.75),
      height: '16%',
      backgroundColor: theme.palette.primary.light,
      borderRadius: theme.spacing(0.5),
      pointerEvents: 'none',
      zIndex: 2,
    },
    '&::-webkit-scrollbar': {
      display: 'block',
      width: theme.spacing(1.5),
    },
    '&::-webkit-scrollbar-track': {
      backgroundColor: theme.palette.background.default,
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: theme.palette.primary.light,
      border: `${theme.spacing(0.25)} solid ${theme.palette.background.default}`,
      borderRadius: theme.spacing(0.75),
    },
  },
}));

type AutocompleteProps = {
  t: TFunction;
  options: OrderListingType[];
  loading: boolean;
  updateSearchText: (text: string) => void;
  autoFocus?: boolean;
};

const Autocomplete: FC<AutocompleteProps> = ({
  t,
  options,
  loading,
  updateSearchText,
  autoFocus = false,
}) => {
  const classes = useStyles();
  const history = useHistory();
  const theme = useTheme<any>();
  const [open, setOpen] = useState(loading || options.length > 0);
  const [inputValue, setInputValue] = useState('');
  const [selectedOption, setSelectedOption] = useState(null);

  const [previousSearch, setPreviousSearch] = useState({ loading, count: options.length });
  if (loading !== previousSearch.loading || options.length !== previousSearch.count) {
    setPreviousSearch({ loading, count: options.length });
    if (loading || options.length > 0) {
      setOpen(true);
    }
  }

  const onInputChange = (
    _: React.ChangeEvent<{}>,
    value: string,
    reason: AutocompleteInputChangeReason
  ) => {
    if (reason === 'input') {
      setInputValue(value);
      updateSearchText(value);
    }

    if (reason === 'clear') {
      setInputValue(value);
      updateSearchText('');
    }
  };

  const onChange = (_: React.ChangeEvent<{}>, option: OrderListingType | null) => {
    if (option !== null) {
      localStorage.setItem(CURRENT_PATH, JSON.stringify({ view: DEFAULT_ROUTE, param: '' }));
      history.push(`/orders/details/${option?._id}/${DEFAULT_ROUTE}`);
    }
  };

  return (
    <MUIAutocomplete
      value={selectedOption}
      inputValue={inputValue}
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      options={options}
      classes={{ option: classes.option }}
      filterOptions={(option) => option}
      loading={loading}
      loadingText={t('common:searching')}
      getOptionLabel={(option) => option.order_id}
      onInputChange={onInputChange}
      onChange={onChange}
      noOptionsText={t('common:no-search-orders')}
      renderInput={(props) => (
        <TextField
          {...props}
          ref={props.InputProps.ref}
          autoFocus={autoFocus}
          disabled={loading}
          placeholder={t('common:find-order')}
          inputProps={{
            ...props.inputProps,
            disabled: loading,
            maxLength: 40,
          }}
          InputLabelProps={{ shrink: true }}
          // eslint-disable-next-line react/jsx-no-duplicate-props
          InputProps={{
            ...props.InputProps,
            disableUnderline: true,
            endAdornment: loading ? (
              <InputAdornment position="end">
                <CircularProgress
                  color="secondary"
                  size={18}
                  thickness={5}
                  aria-label={t('common:searching')}
                  style={{ color: theme.palette.secondary.main }}
                />
              </InputAdornment>
            ) : props.InputProps.endAdornment,
            startAdornment: (
              <InputAdornment position="start" sx={{ color: 'inherit' }}>
                <FontAwesomeIcon icon="search" size="1x" />
              </InputAdornment>
            ),
            classes: {
              root: classes.toolbarTextfield,
              focused: classes.toolbarTextfieldFocused,
            },
          }}
        />
      )}
      renderOption={({ key, ...props }, option) => {
        const moneyItems = [
          Number(option.purchase_price) > 0 && (
            <Box component="span" className={classes.moneyItem} key="purchase-price">
              <Typography component="span" variant="subtitle2" className={classes.renderLabel}>
                {t('orders:purchase-price')}
              </Typography>
              <Typography
                component="span"
                variant="subtitle1"
                className={`${classes.renderValue} ${classes.numericValue}`}
              >
                {formatSearchCurrency(Number(option.purchase_price))}
              </Typography>
            </Box>
          ),
          Number(option.loan_amount) > 0 && (
            <Box component="span" className={classes.moneyItem} key="loan-amount">
              <Typography component="span" variant="subtitle2" className={classes.renderLabel}>
                {t('orders:loan-amount')}
              </Typography>
              <Typography
                component="span"
                variant="subtitle1"
                className={`${classes.renderValue} ${classes.numericValue}`}
              >
                {formatSearchCurrency(Number(option.loan_amount))}
              </Typography>
            </Box>
          ),
        ].filter(Boolean);

        return (
          <li key={key} {...props}>
            <Box className={classes.renderContainer}>
              <Typography
                variant="subtitle1"
                className={`${classes.renderLine} ${classes.renderValue} ${classes.numericValue}`}
              >
                {option.order_id}
              </Typography>
              <Typography variant="body2" className={`${classes.renderLine} ${classes.renderValue}`}>
                {option.address || t('common:no-property-address')}
              </Typography>
              {moneyItems.length > 0 && (
              <Box
                className={classes.renderLine}
                display="flex"
                flexWrap="wrap"
                style={{ columnGap: theme.spacing(1), rowGap: theme.spacing(0.5) }}
              >
                {moneyItems}
              </Box>
              )}
            </Box>
          </li>
        );
      }}
      PopperComponent={(props) => (
        <Popper
          {...props}
          style={{
            width: 'min(600px, calc(100vw - 32px))',
            maxWidth: 'calc(100vw - 32px)',
          }}
          placement="bottom-start"
        />
      )}
      PaperComponent={(props) => (
        <Paper {...props} style={{ backgroundColor: theme.palette.background.default }} />
      )}
      ListboxComponent={forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLElement>>((props, ref) => (
        <div
          {...props}
          ref={ref}
          role="listbox"
          className={`${props.className || ''} ${classes.resultsListbox}`}
        >
          {props.children}
        </div>
      ))}
    />
  );
};

export default Autocomplete;
